// GhlSyncPanel — ticket 122 S5. Owner-only view of the GHL read-sync scheduler:
// health pill, last-sync line, queue/daily-limit/error tiles and a manual
// "Sync now" button. Mirrors AccountsOverview's Panel/Loading/ErrorBox/useGet
// pattern locally rather than importing (that file is not restructured for this).
import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Loader2, Info } from 'lucide-react';
import { api } from '../api';
import KpiTile from './KpiTile';
import { fromApi } from '../kit/time';

const relTime = (iso) => {
  if (!iso) return 'never';
  const mins = Math.max(0, Math.round((Date.now() - fromApi(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const STATE_PILL = {
  ok: { label: 'Healthy', bg: '#DCFCE7', color: '#166534' },
  stale: { label: 'Stale', bg: '#FEF3C7', color: '#92400E' },
  auth: { label: 'Auth failure', bg: '#FEE2E2', color: '#991B1B' },
};
const OFF_PILL = { bg: '#F1F5F9', color: '#64748B' };

function Panel({ title, sub, testid, children, action }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-8" data-testid={testid}>
      <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-100">
        <div className="flex-1 min-w-0">
          <h2 className="font-serif text-lg font-bold leading-tight" style={{ color: '#070B5F' }}>{title}</h2>
          {sub && <p className="text-[12px] text-slate-500 mt-0.5">{sub}</p>}
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

const Loading = ({ testid, label }) => (
  <div className="text-slate-500 flex items-center gap-2 text-sm" data-testid={testid}>
    <Loader2 size={16} className="animate-spin" /> {label}
  </div>
);

const ErrorBox = ({ testid, error }) => (
  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid={testid}>
    {String(error)}
  </div>
);

const detailOf = (e, fallback) => e?.response?.data?.detail || fallback;

function useGet(url, fallback, reloadKey) {
  const [state, setState] = useState({ data: null, error: null });
  useEffect(() => {
    let alive = true;
    api.get(url)
      .then((r) => { if (alive) setState({ data: r.data, error: null }); })
      .catch((e) => { if (alive) setState({ data: null, error: detailOf(e, fallback) }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, fallback, reloadKey]);
  return state;
}

function statePill(data) {
  if (!data.tenant_id || !data.config_complete) return { label: 'Not configured', ...OFF_PILL };
  if (data.enabled === false) return { label: 'Scheduler off', ...OFF_PILL };
  const state = data.health?.state;
  return STATE_PILL[state] || { label: 'Unknown', ...OFF_PILL };
}

function lastSyncLine(data) {
  const kinds = ['full', 'incremental', 'id_diff'];
  let best = null;
  for (const k of kinds) {
    const row = data.last_ok?.[k];
    if (row?.finished_at && (!best || row.finished_at > best.finished_at)) {
      best = { ...row, kind: k };
    }
  }
  if (!best) return 'Last successful sync: never';
  return `Last successful sync: ${relTime(best.finished_at)} (${best.kind})`;
}

function countsSummary(counts) {
  const parts = Object.entries(counts || {})
    .filter(([, v]) => Number.isInteger(v))
    .map(([k, v]) => `${k}: ${v}`);
  return parts.length ? parts.join(', ') : 'no changes';
}

export default function GhlSyncPanel() {
  const [reloadKey, setReloadKey] = useState(0);
  const { data, error } = useGet('/super/integrations/ghl', 'Could not load GHL sync status', reloadKey);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  const syncNow = useCallback(async () => {
    setSyncing(true);
    setResult(null);
    try {
      const r = await api.post('/super/integrations/ghl/sync-now');
      const counts = countsSummary(r.data.counts);
      setResult(`Run ${r.data.run_id}: ${r.data.status} (${counts})`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) setResult('A sync is already running');
      else if (status === 503) setResult('GHL is not configured on this server');
      else setResult('Sync failed');
    } finally {
      setSyncing(false);
      refetch();
    }
  }, [refetch]);

  return (
    <Panel title="GHL sync" sub="Read-only sync from GoHighLevel — contacts, opportunities and notes." testid="ghl-sync-panel">
      {error && <ErrorBox testid="ghl-sync-error" error={error} />}
      {!error && !data && <Loading testid="ghl-sync-loading" label="Loading GHL sync status…" />}
      {data && <GhlSyncBody data={data} syncing={syncing} onSyncNow={syncNow} result={result} />}
    </Panel>
  );
}

function GhlSyncBody({ data, syncing, onSyncNow, result }) {
  const pill = statePill(data);
  const notConfigured = !data.tenant_id || !data.config_complete;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold"
          style={{ background: pill.bg, color: pill.color }}
          data-testid="ghl-sync-state-pill"
        >
          {pill.label}
        </span>
        {!data.scheduler_running && data.start_reasons?.length > 0 && (
          <span className="text-[11px] text-slate-500" data-testid="ghl-sync-start-reasons">
            {data.start_reasons.join(', ')}
          </span>
        )}
      </div>

      {!notConfigured && (
        <>
          <p className="text-sm text-slate-600" data-testid="ghl-sync-last-line">{lastSyncLine(data)}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <KpiTile
              label="Open queue"
              value={Object.values(data.queue_open || {}).reduce((a, b) => a + b, 0)}
              sub={Object.entries(data.queue_open || {}).map(([k, v]) => `${k}: ${v}`).join(', ') || 'none'}
              icon={Info}
              testid="ghl-kpi-queue"
            />
            <KpiTile
              label="Daily remaining"
              value={data.daily_remaining ?? '—'}
              sub={data.daily_remaining_at ? `as of ${relTime(data.daily_remaining_at)}` : 'none'}
              icon={Info}
              testid="ghl-kpi-daily-remaining"
            />
            <KpiTile
              label="Last error"
              value={data.last_error ? data.last_error.error_class : 'none'}
              sub={data.last_error
                ? `${data.last_error.error_status ?? ''} · ${data.last_error.kind} · ${relTime(data.last_error.finished_at)}`
                : 'none'}
              icon={Info}
              testid="ghl-kpi-last-error"
            />
          </div>

          <div>
            <button
              onClick={onSyncNow}
              disabled={syncing}
              className="btn-outline inline-flex items-center gap-1.5"
              data-testid="ghl-sync-now"
            >
              {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sync now
            </button>
            {result && (
              <p className="text-sm text-slate-600 mt-2" data-testid="ghl-sync-result">{result}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
